import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Menu } from 'lucide-react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const Navbar: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { scrollY, scrollYProgress } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  // Handle scroll events to show/hide back to top button
  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 300) {
      setShowBackToTop(true);
      setIsScrolled(true);
    } else {
      setShowBackToTop(false);
      setIsScrolled(false);
    }
  });

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false);
    }
    // Prevent background scroll when menu is open
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isMenuOpen]);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Navigation links with proper section IDs
  const navLinks = [
    { name: 'Usluge', href: '#problems-section' },
    { name: 'O nama', href: '#about-section' },
    { name: 'Kontakt', href: '#contact-section' },
  ];

  return (
    <>
      <nav className={cn(
        "fixed top-0 left-0 w-full py-4 px-4 md:px-6 lg:px-16 flex items-center justify-between z-40 transition-all duration-300",
        isScrolled ? "bg-black/80 backdrop-blur-md shadow-md" : "bg-transparent"
      )}>
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" aria-label="Synorio Home">
            <img 
              src="/lovable-uploads/logo.png" 
              alt="Synorio Logo" 
              className="h-8 md:h-10 w-auto object-contain rounded-full"
              width={40}
              height={40}
            />
            <span className="text-lg md:text-xl font-ibm font-bold text-white tracking-tighter">Synorio</span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a 
              key={link.name}
              href={link.href} 
              className="text-gray-300 hover:text-white transition-colors"
              onClick={(e) => {
                e.preventDefault();
                const element = document.querySelector(link.href);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                  window.history.pushState(null, '', link.href);
                }
              }}
            >
              {link.name}
            </a>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2 text-white"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <Menu size={24} />
        </button>

        {/* Mobile Navigation Menu */}
        <motion.div 
          className={cn(
            "fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center",
            isMenuOpen ? "block" : "hidden"
          )}
          initial={false}
          animate={isMenuOpen ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button 
            className="absolute top-4 right-4 p-4 sm:p-5 text-white text-2xl sm:text-3xl rounded-full focus:outline-none focus:ring-2 focus:ring-synorio-purple"
            onClick={toggleMenu}
            aria-label="Close menu"
          >
            ✕
          </button>
          <div className="flex flex-col items-center gap-10 sm:gap-12 mt-8">
            {navLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href} 
                className="text-2xl sm:text-3xl text-gray-200 hover:text-white transition-colors px-8 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-synorio-purple"
                onClick={toggleMenu}
                tabIndex={0}
              >
                {link.name}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Scroll Progress Indicator */}
        <motion.div 
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-synorio-purple/50 via-synorio-purple to-synorio-purple/50 origin-left z-50"
          style={{ scaleX: scrollYProgress }}
        />
      </nav>

      {/* Back to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 md:bottom-8 md:right-8 p-3 bg-synorio-purple rounded-full shadow-lg z-50 hover:bg-synorio-darkpurple transition-colors"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: showBackToTop ? 1 : 0,
          scale: showBackToTop ? 1 : 0.8,
          y: showBackToTop ? 0 : 20
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Back to top"
      >
        <ArrowUp size={20} className="text-white" />
      </motion.button>
    </>
  );
};

export default Navbar;
